import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import {
  getScaledFont,
  getMigratedStyleProps,
  isSupportSpExperimentOn,
} from '@wix/editor-elements-common-utils';
import {
  getInputHeight,
  getLabelPadding,
  getRequiredIndicationDisplay,
  getTextPadding,
} from '@wix/editor-elements-common-utils/src/commons/inputUtils';
import {
  ComboBoxInputDefinition,
  ComboBoxInputSdkData,
  IComboBoxInputMapperProps,
  IComboBoxInputCSSVars,
} from '../ComboBoxInput.types';
import { migratedFields, translationKeys } from '../constants';

export const props = withCompInfo<
  IComboBoxInputMapperProps,
  ComboBoxInputDefinition,
  IComboBoxInputMapperProps
>()(
  ['compProps', 'translate', 'deviceType'],
  ({ compProps, translate, deviceType }, carmiData) => {
    return {
      ...carmiData,
      autocomplete: compProps.autocomplete,
      deviceType,
      errorMessageType: compProps.errorMessageType,
      translations: {
        errorMessage: translate(
          translationKeys.NAMESPACE,
          translationKeys.INLINE_ERROR_MESSAGE_DEFAULT_ERROR_MESSAGE,
        ),
      },
    };
  },
);

const minimumLabelFontSize = 16;
const justifyContent = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
} as const;

export const css = withCompInfo<
  IComboBoxInputCSSVars,
  ComboBoxInputDefinition,
  {}
>()(
  [
    'compProps',
    'compLayout',
    'styleProperties',
    'isMobileView',
    'hasResponsiveLayout',
    'siteFonts',
    'formatCssValue',
    'experiments',
    'siteFontsSpxResolved',
  ],
  ({
    compProps,
    compLayout,
    styleProperties,
    isMobileView,
    hasResponsiveLayout,
    siteFonts,
    formatCssValue,
    experiments,
    siteFontsSpxResolved,
  }) => {
    const { inputMobileFontSize, labelMobileFontSize, inputHeightMobile } =
      compProps;
    const isSupportSpOn = isSupportSpExperimentOn(experiments);

    const compInfo = {
      compLayout,
      styleProperties,
      siteFonts,
      isMobileView,
      siteFontsSpxResolved: isSupportSpOn ? siteFontsSpxResolved : undefined,
    };

    const migratedStyleProps = isSupportSpOn
      ? getMigratedStyleProps({
          compProps,
          styleProperties,
          migratedFields,
        })
      : compProps;

    const {
      labelMargin,
      labelPadding,
      textAlignment,
      textPadding,
      inputHeight,
    } = migratedStyleProps;

    const { spx } = styleProperties;
    const dropdownOptionJustifyContent =
      justifyContent[(textAlignment as keyof typeof justifyContent) || 'left'];

    const sharedVars = {
      '--fnt': getScaledFont(compInfo, 'fnt', inputMobileFontSize),
      '--fntlbl': getScaledFont(
        compInfo,
        'fntlbl',
        labelMobileFontSize,
        minimumLabelFontSize,
      ),
      '--arrowLeftPosition': textAlignment === 'right' ? '0' : 'auto',
      '--arrowRightPosition': textAlignment === 'right' ? 'auto' : '0',
      '--labelMarginBottom': isSupportSpOn
        ? formatCssValue(labelMargin, spx)
        : `${labelMargin}px`,
      '--requiredIndicationDisplay':
        getRequiredIndicationDisplay(styleProperties),
      '--labelPadding': getLabelPadding(
        {
          labelPadding,
          textAlignment,
        },
        isSupportSpOn ? formatCssValue : undefined,
        spx,
      ),
      '--dir': textAlignment === 'right' ? 'rtl' : 'ltr',
      '--textAlign': textAlignment!,
      '--textPadding': getTextPadding(
        {
          textAlignment,
          textPadding,
        },
        isSupportSpOn ? formatCssValue : undefined,
        spx,
      ),
      '--dropdownOptionJustifyContent': dropdownOptionJustifyContent,
    };

    if (hasResponsiveLayout) {
      return sharedVars;
    }

    return {
      ...sharedVars,
      height: 'auto',
      '--inputHeight': getInputHeight(
        {
          inputHeightProps: { inputHeightMobile, inputHeight },
          compLayout,
          isMobileView,
        },
        isSupportSpOn ? formatCssValue : undefined,
        spx,
      ),
    };
  },
);

export const sdkData = withCompInfo<
  ComboBoxInputSdkData,
  ComboBoxInputDefinition
>()(['experiments'], ({ experiments }) => {
  const { 'specs.thunderbolt.dropdownOptionStyle': optionStyleExperiment } =
    experiments;
  const isExperimentOpen = (experiment: boolean | string) =>
    experiment && optionStyleExperiment !== 'false';

  return {
    isOptionStyleEnabled: isExperimentOpen(optionStyleExperiment),
  };
});

const stateRefs = withStateRefsValues(['scopedClassName']);

export default createComponentMapperModel({ props, css, sdkData, stateRefs });
